import { useRef, useEffect, useState } from "react";
import { Compass, Landmark, Banknote, Users, Cpu, Scale, Leaf, ArrowRight, Download, LayoutDashboard } from "lucide-react";
import { readWmToken } from "@/lib/wmToken";

const ICONS = {
  political: Landmark, economic: Banknote, social: Users,
  technological: Cpu, legal: Scale, environmental: Leaf,
};

const ACCENT = {
  political: "#e93e36", economic: "#f4b847", social: "#7cd4b0",
  technological: "#4aa0a4", legal: "#b084ff", environmental: "#4ec9a0",
};

export default function PestleResults({ data }) {
  const a = data.analysis;
  const keys = ["political", "economic", "social", "technological", "legal", "environmental"];
  const pdfRef = useRef(null);
  const [isMember, setIsMember] = useState(false);
  useEffect(() => { setIsMember(!!readWmToken()); }, []);

  const downloadPDF = async () => {
    if (!pdfRef.current) return;
    try {
      const mod = await import("html2pdf.js");
      const html2pdf = mod.default || mod;
      const opt = {
        margin: [12, 10],
        filename: `catch-the-wave-${data.id.slice(0, 8)}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#f5efe3" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
      html2pdf().set(opt).from(pdfRef.current).save();
    } catch (e) {
      console.error("PDF download failed", e);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="pestle-results">
      <div ref={pdfRef} className="pdf-root">
        <div className="flex items-center gap-2 text-xs tracking-[0.3em] font-bold text-[var(--coral)]"><Compass className="w-4 h-4"/> YOUR SCAN IS IN</div>
        <h2 className="font-display font-black text-5xl md:text-6xl mt-3 leading-[1] text-[var(--ink)]" data-testid="results-headline">{a.headline}</h2>
        <p className="mt-6 text-lg text-[var(--ink)]/80 max-w-3xl">{a.sector_snapshot}</p>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {keys.map((k) => {
            const s = a[k]; if (!s) return null;
            const Icon = ICONS[k];
            return (
              <div key={k} className="bg-white border-2 border-[var(--ink)] rounded-3xl p-6 hover-lift" data-testid={`pestle-${k}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: ACCENT[k] }}>
                    <Icon className="w-5 h-5 text-[var(--ink)]" />
                  </div>
                  <h3 className="font-display font-black text-2xl">{s.title}</h3>
                </div>
                <p className="mt-4 text-sm text-[var(--ink)]/80">{s.summary}</p>

                <div className="mt-5">
                  <div className="text-[11px] tracking-widest font-bold text-[var(--ink)]/60">STRENGTHS</div>
                  <ul className="mt-2 space-y-1.5">
                    {s.strengths?.map((b, i) => (<li key={i} className="text-sm flex gap-2"><span className="text-[var(--mint)] font-bold">+</span>{b}</li>))}
                  </ul>
                </div>
                <div className="mt-4">
                  <div className="text-[11px] tracking-widest font-bold text-[var(--ink)]/60">WEAKNESSES</div>
                  <ul className="mt-2 space-y-1.5">
                    {s.weaknesses?.map((b, i) => (<li key={i} className="text-sm flex gap-2"><span className="text-[var(--coral)] font-bold">−</span>{b}</li>))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-3xl bg-[var(--ink)] text-white p-8 md:p-10 grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.3em] font-bold text-[var(--sun)]">THE WAVE TO CATCH</div>
            <p className="font-display font-bold text-2xl md:text-3xl mt-3 leading-tight" data-testid="wave-to-catch">{a.wave_to_catch}</p>
            <p className="mt-5 text-white/80">{a.next_step}</p>
          </div>
          <div className="md:col-span-2 space-y-3 no-print">
            {isMember ? (
              <a href="https://awake.wearetheweave.co.uk/dashboard" target="_blank" rel="noreferrer" className="btn-coral w-full inline-flex items-center justify-center gap-2" data-testid="results-back-dashboard">
                <LayoutDashboard className="w-4 h-4"/> Back to your dashboard
              </a>
            ) : (
              <a href="https://awake.wearetheweave.co.uk/join" target="_blank" rel="noreferrer" className="btn-coral w-full inline-flex items-center justify-center gap-2" data-testid="results-join-club">Join The 1% Club <ArrowRight className="w-4 h-4"/></a>
            )}
            <button onClick={downloadPDF} className="w-full inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold rounded-full px-6 py-3" data-testid="results-download">
              <Download className="w-4 h-4"/> Download as PDF
            </button>
            <p className="text-xs text-white/60 text-center">A copy has been emailed to you under UK GDPR.</p>
          </div>
        </div>

        <div className="mt-8 p-5 rounded-2xl border border-[var(--ink)]/20 bg-white text-xs leading-relaxed text-[var(--ink)]/75" data-testid="results-disclaimer">
          <div className="text-[10px] tracking-[0.3em] font-bold text-[var(--ink)]/60 mb-2">ABOUT THIS SCAN</div>
          <p className="mb-2">
            Catch the Wave is a free, AI-generated PESTLE Light overview. It is a starting point for strategic thinking — <strong>not</strong> professional, financial, legal, investment or regulatory advice — and may contain errors or omissions. Do not rely on it as the sole basis for any decision; seek qualified professional advice first.
          </p>
          <p>
            The Weave, its members and partners accept no liability for any loss or consequence arising from use of, or reliance on, this scan. Use is subject to our <a href="https://www.wearetheweave.co.uk/privacy-policy" className="underline">Privacy Policy</a> and Terms at <a href="https://www.wearetheweave.co.uk" className="underline">wearetheweave.co.uk</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
